import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import Tag from 'antd/lib/tag';
import VerticalAlignBottomOutlined from '@ant-design/icons/VerticalAlignBottomOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';

import { memoize } from '@common/typescript/utils/memoize';
import { getAvatar } from '@common/react/components/Utils/Utils';
import { Chat, ChatMessage, ChatMessageType } from '@common/react/objects/Chat';
import * as ChatsState from '@common/react/store/Chats';
import { getUserName } from '@common/react/utils/utils';
import { BaseUserWithAvatar } from '@common/react/objects/BaseUser';
import { BaseApplicationState } from '@common/react/store';
import { dateFormat, dateTimeFormat } from '@common/typescript/Utils';
import { File } from '@common/typescript/objects/FileInterface';
import {
	defaultTransformFiltersBeforeHandleUrl,
	ItemsProvider,
	useItemsProviderContext
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import {
	ChatMessageActions,
	ChatMessageActionsComponent,
	ChatMessageActionsDictionary,
	ChatMessageTypeDictionary,
	ChatSettingsProviderContext,
	useChatSettingsProviderContext
} from '@common/react/components/Chat/ChatSettingsProvider';
import Loader from '@common/react/components/Core/LoadingProvider/Loader';
import useRequest from '@common/react/hooks/useRequest';
import { deleteConfirmation } from '@common/react/components/Modal/Modal';
import { imageExtensions } from '@common/react/components/Chat/Chats';

type ListProps = OwnProps;

type Actions = ChatsState.ChatsActionCreators<BaseUserWithAvatar, BaseApplicationState<BaseUserWithAvatar>>;

interface OwnProps {
	actions: Actions;
	chat: Chat;
	user: BaseUserWithAvatar;
	context: ChatSettingsProviderContext;
	setEdit: (message) => void;
	editMessage?: ChatMessage | null;
	onMentionClick?: (contact: BaseUserWithAvatar) => void;
	loader?: React.ReactElement;
	onImageClick?: (e, file: File) => void;
	getUserAvatar?: (user) => React.ReactNode;
}

interface ChatItemProps {
	message: ChatMessage;
	withRemoteId: boolean;
	fromUser: boolean;
	withoutBadge: boolean;
	user: BaseUserWithAvatar;
	remove: () => void;
	update: (message) => void;
	edit: (message) => void;
	isEdit: boolean;
	onMouseEnter?: (message: ChatMessage) => void;
	contacts: BaseUserWithAvatar[];
	onImageClick?: (e, file: File) => void;
	getUserAvatar?: (user) => React.ReactNode;
	lastVideoCallId?: number;
	messageActions?: ChatMessageActionsComponent[];
}

const getMessageUser = memoize((id: number, contacts: BaseUserWithAvatar[]) => contacts.find((contact: BaseUserWithAvatar) => contact.id === id));

export const getFilesLayout = (files: File[], onClick?: (e, file: File) => void): JSX.Element | null => {
	const filteredFiles = files.filter(f => f.file.name !== 'video_message.webm' && f.file.name !== 'voice_message.mp3');

	if (filteredFiles.length <= 0) {
		return null;
	}

	return <>
		<br/>
		<div className="chat-uploaded-files" style={{flexWrap: 'wrap'}}>
			{filteredFiles
				.map(f => (
					<a key={f.file.id} href={f.file.src} target="_blank" download>
						<Tag className="chat-file-tag">
							<div style={{display: 'flex', flexDirection: 'column'}}>
								<div>
									<FileOutlined/>&nbsp;&nbsp;
									{f.file.name}&nbsp;&nbsp;<VerticalAlignBottomOutlined/>&nbsp;&nbsp;
								</div>
								{imageExtensions.includes(f.file.extension?.toLowerCase()) && <div>
									<img
										onClick={e => onClick && onClick(e, f)}
										className="chat-file-tag-image"
										src={f.file.src} alt={f.file.name}/>
								</div>}
							</div>
						</Tag>
					</a>
				))
			}
		</div>
	</>;
};

const ChatMessageItem: React.FC<ChatItemProps> = ({message, withoutBadge, fromUser, user, onMouseEnter, contacts, onImageClick, ...rest}) => {
	const {withRemoteId, lastVideoCallId} = rest;

	const className = `chat-message-list-component__item ${
		fromUser ? 'chat-message-list-component__item_you' : ''} ${
		withoutBadge ? 'chat-message-list-component__item_withoutBadge' : ''} ${
		message.viewed ? '' : 'chat-message-list-component__item_unviewed'} ${
		rest.isEdit ? 'chat-message-list-component__item_edit' : ''
	}`;

	const {getUserAvatar = user => <div
		className="chat-message-list-component__item-avatar"
		style={{backgroundImage: `url(${getAvatar(user)})`}}/>} = rest;

	return (message.chatMessageType !== ChatMessageType.Email || withRemoteId) ?
		<li className={className} onMouseEnter={onMouseEnter ? () => onMouseEnter(message) : undefined}>
			<div className="chat-message-list-component__item_inner">
			{!withoutBadge && user &&
				<React.Fragment>
					{getUserAvatar(user)}
					<div className="chat-message-list-component__item-author">{getUserName(user)}</div>
				</React.Fragment>
			}
			{!withoutBadge && !user && withRemoteId
				&& <div className="chat-message-list-component__item-author">{user ? getUserName(user) : 'DELETED'}</div>}
			<div
				className="chat-message-list-component__item-time">{dateFormat(message.time)} {dateTimeFormat(message.time)}</div>
				{ChatMessageTypeDictionary[message.chatMessageType]({
					message,
					contacts,
					onImageClick,
					withRemoteId,
					onMouseEnter,
					lastVideoCallId
				})}
			</div>
			{
				rest.messageActions?.length ?
					<div className="text-end chat-message-list-component__item-actions">
					{rest.messageActions.map((action) => {
						const props = {
							message,
							edit: rest.edit,
							isEdit: rest.isEdit,
							remove: rest.remove,
							update: rest.update,
							fromUser
						};

						if (typeof action === 'function') {
							return action(props);
						}

						if (action !== ChatMessageActions.Remove && message.chatMessageType !== ChatMessageType.Regular) {
							return null;
						}
						if (action === ChatMessageActions.Remove && [ChatMessageType.VoiceMessage, ChatMessageType.VideoMessage].includes(message.chatMessageType)) {
							return ChatMessageActionsDictionary[action](props);
						}
						return ChatMessageActionsDictionary[action](props);
					})}
				</div>
					: null
			}
		</li> : null;
};

export const ScrollTo: React.FC<{refreshId: number | string | null}> = ({refreshId}) => {
	const ref = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		const scrollContainer = ref.current;

		if (scrollContainer) {
			scrollContainer.scrollIntoView();
		}
	}, [refreshId]);

	return <div ref={ref} id="chatScrollToBottom"/>;
};

const ChatMessageList: React.FC<ListProps & {initLoad?: boolean}> = (props) => {
	const {user, chat, loader, onMentionClick, actions, onImageClick, getUserAvatar, context: chatSettingsContext} = props;
	const {setEdit, editMessage} = props;
	const request = useRequest();

	const lastScroll = React.useRef(0);

	const [scrollId, setScrollId] = React.useState<number | null>(null);

	const context = useItemsProviderContext<ChatMessage>();
	const {state: {items, pagination, loading, filters}, actions: {load, loadMore}} = context;

	const {state: {requests, withRemoteId, chatStoreSettings: {getMessages}, messageActions}} = chatSettingsContext;

	const messages = useSelector((state: BaseApplicationState<BaseUserWithAvatar>) => {
		return getMessages(chat.id)(state);
	}, shallowEqual);

	React.useEffect(() => {
		if ((filters.chatId !== chat.id || props.initLoad) && !messages) {
			const otherChat = chat.contacts.every(contact => (contact as any).remoteId !== user.id);
			const userId = otherChat ? (chat.contacts as any).find(contact => contact.remoteId && contact.remoteId > 0)?.remoteId : undefined;

			load({chatId: chat.id, userId: withRemoteId ? userId || 0 : undefined}, false, false, false, true)
				.then((res) => {
					res.list.length > 0 && setScrollId(res.list[0].id);
				});
		} else {
			items.length > 0 && setScrollId(items[items.length - 1].id);
		}
	}, [chat.id]);

	React.useEffect(() => {
		if (items.length > 0 && items[items.length - 1].id) {
			setScrollId(items[items.length - 1].id);
		}
	}, [items]);

	const clickHandler = (e) => {
		if (e.target && e.target.nodeName === 'A' && e.target.className === 'chat-component__mention' && e.target.dataset.id) {
			const contact = chat.contacts.find(item => item.id === +e.target.dataset.id);
			contact && onMentionClick && onMentionClick(contact);
		}
	};

	const setViewed = (message: ChatMessage) => {
		request(requests.chatMessageAccess, {
			viewed: true,
			message: message.id
		});
	};

	const onScroll = (event) => {
		const scrollTop = (event.target as HTMLUListElement).scrollTop;
		if (items.length < pagination.total && !loading && scrollTop < 100 && lastScroll.current - scrollTop > 0) {
			const otherChat = chat.contacts.every((contact: any) => contact.remoteId !== user.id);
			const userId = otherChat ? (chat.contacts as any).find(contact => contact.remoteId && contact.remoteId > 0)?.remoteId : undefined;

			loadMore({chatId: chat.id, userId: withRemoteId ? userId || 0 : undefined}, true);
		}

		lastScroll.current = scrollTop;
	};

	const self = withRemoteId ? chat.contacts.find(contact => (contact as any).remoteId === user.id) : user;
	const contacts = chat.contacts.filter(contact => withRemoteId ? (contact as any).remoteId !== user.id : contact.id !== user.id);
	const lastVideoCallId = React.useMemo(() => {
		return items.length > 0 ? [...items]
			.reverse()
			.find(item => item.chatMessageType === ChatMessageType.VideoChat)?.id : undefined;
	}, [items]);

	const update = (message, params) => {
		requests.updateMessage && request(requests.updateMessage, {
			id: message.id,
			...params
		})
			.then(() => {
				actions.updateMessage({ id:message.id, chatId: message.chatId, ...params});
			});
	};

	const remove = (message) => {
		if (messages && requests.removeMessage) {
			return request(requests.removeMessage, {
				id: message.id,
				deleted: true
			})
				.then(() => {
					actions.removeMessage(message, message.chatId);
				});
		}
	};

	const handleRemove = (message) => {
		if (chatSettingsContext.state.removeMessageConfirmation) {
			deleteConfirmation(
				() => remove(message),
				'This message will be permanently deleted and cannot be recovered. Are you sure?'
			);
		} else {
			remove(message);
		}
	};

	return <div className="chat-component-list-content" onClick={clickHandler}>
		{(items && items.length === 0 && loading)
			? <div className="chat-message-list-component__loading"><Loader /></div>
			: <ul className="chat-message-list-component" onScroll={onScroll}>
				{items.map((item, index) => {
					const withoutBadge: boolean = index > 0 && item.userId === items[index - 1].userId;
					const messageUser = item.userId === user.id ? user : getMessageUser(item.userId, contacts);

					return <ChatMessageItem
						message={item}
						fromUser={item.userId === self?.id}
						withoutBadge={withoutBadge}
						user={messageUser}
						key={item.id}
						contacts={chat.contacts}
						onMouseEnter={!item.viewed && self && self.id !== item.userId ? setViewed : undefined}
						onImageClick={onImageClick}
						getUserAvatar={getUserAvatar}
						withRemoteId={withRemoteId}
						lastVideoCallId={lastVideoCallId}
						messageActions={messageActions}
						edit={setEdit}
						isEdit={item.id === editMessage?.id}
						remove={() => handleRemove(item)}
						update={message => update(item, message)}
					/>;
				})}
				<ScrollTo refreshId={`${scrollId}-${chat?.id || 0}`}/>
			</ul>
		}
	</div>;
};

const ChatMessageListWrapper: React.FC<Omit<OwnProps, 'context'>> = (props) => {
	const context = useChatSettingsProviderContext();

	if (!context?.state) throw 'need ChatSettingsContext';

	const {state: {requests, chatStoreSettings: {getMessages}}} = context;

	const messages = useSelector(getMessages(props.chat.id), shallowEqual);

	return <ItemsProvider
		items={messages?.list}
		syncItems={messages?.list}
		type={requests.chatMessage}
		transformFiltersBeforeHandleUrl={filters => ({
			...defaultTransformFiltersBeforeHandleUrl(filters),
			count: undefined
		})}
		pagination={{total: messages?.count, page: 1, pageSize: 20, pageSizeOptions: [10, 20, 50] }}
		filters={{chatId: props.chat.id}}
		onItemsChange={(items, filters) => props.actions.setMessages({
			list: items,
			offset: items.length - (filters?.pageSize || 20),
			execution: 0,
			count: items.length
		}, filters.chatId)}
	>
		<ChatMessageList
			{...props}
			context={context}
			initLoad={!messages}
		/>
	</ItemsProvider>;
};

export default ChatMessageListWrapper;
